import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { NotificationsBell } from '@/components/NotificationsBell';
import { UserAvatar } from '@/components/common/UserAvatar';
import {
  Mountain, LayoutDashboard, CalendarDays, Heart, User, Hotel,
  UtensilsCrossed, Menu, LogOut, Compass, Ticket, Wrench, Wallet,
  ShoppingCart, Receipt, Store, Sparkles, Landmark, PartyPopper, TreePine, GraduationCap,
} from 'lucide-react';

const NAV = [
  { label: 'Dashboard', to: '/cliente', icon: LayoutDashboard, testid: 'customer-nav-dashboard', end: true },
  { label: 'Prenotazioni', to: '/cliente/prenotazioni', icon: CalendarDays, testid: 'customer-nav-bookings' },
  { label: 'Biglietti', to: '/cliente/biglietti', icon: Ticket, testid: 'customer-nav-tickets' },
  { label: 'I miei corsi', to: '/cliente/corsi', icon: GraduationCap, testid: 'customer-nav-courses' },
  { label: 'Carrello', to: '/cliente/carrello', icon: ShoppingCart, testid: 'customer-nav-cart', badge: true },
  { label: 'I miei viaggi', to: '/cliente/viaggi', icon: Sparkles, testid: 'customer-nav-trips' },
  { label: 'Ordini', to: '/cliente/ordini', icon: Receipt, testid: 'customer-nav-orders' },
  { label: 'Servizi', to: '/cliente/servizi', icon: Wrench, testid: 'customer-nav-services' },
  { label: 'Escursioni', to: '/cliente/escursioni', icon: Compass, testid: 'customer-nav-excursions' },
  { label: 'Portafoglio', to: '/cliente/portafoglio', icon: Wallet, testid: 'customer-nav-wallet' },
  { label: 'Preferiti', to: '/cliente/preferiti', icon: Heart, testid: 'customer-nav-favorites' },
  { label: 'Profilo', to: '/cliente/profilo', icon: User, testid: 'customer-nav-profile' },
];
const EXPLORE = [
  { label: 'Shop prodotti', to: '/shop', icon: Store, testid: 'customer-nav-explore-shop' },
  { label: 'Esplora Alloggi', to: '/alloggi', icon: Hotel, testid: 'customer-nav-explore-alloggi' },
  { label: 'Esplora Ristoranti', to: '/dove-mangiare', icon: UtensilsCrossed, testid: 'customer-nav-explore-ristoranti' },
  { label: 'Tour nei Borghi', to: '/tour-nei-borghi', icon: Landmark, testid: 'customer-nav-explore-borghi' },
  { label: 'Eventi', to: '/eventi', icon: PartyPopper, testid: 'customer-nav-explore-eventi' },
  { label: 'Parco', to: '/il-parco-della-sila', icon: TreePine, testid: 'customer-nav-explore-parco' },
];

const navClass = ({ isActive }) =>
  [
    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
    isActive
      ? 'bg-accent text-accent-foreground'
      : 'text-foreground/75 hover:bg-accent/60 hover:text-accent-foreground',
  ].join(' ');

function SidebarContent({ user, onLogout, onNavigate, cartCount = 0 }) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between px-5 py-5">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Mountain className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <p className="font-display text-sm font-bold leading-tight">Sila Experiences</p>
            <p className="text-xs text-muted-foreground">Area Clienti</p>
          </div>
        </div>
        <NotificationsBell />
      </div>
      <Separator />
      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        {NAV.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} className={navClass}
            onClick={onNavigate} data-testid={item.testid}>
            <item.icon className="h-4 w-4" aria-hidden="true" />
            <span className="flex-1">{item.label}</span>
            {item.badge && cartCount > 0 && (
              <Badge className="h-5 min-w-5 justify-center rounded-full px-1.5 text-[11px] tabular-nums"
                data-testid="customer-nav-cart-badge">{cartCount}</Badge>
            )}
          </NavLink>
        ))}
        <div className="px-3 pb-1 pt-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><Compass className="h-3.5 w-3.5" />Esplora il network</span>
        </div>
        {EXPLORE.map((item) => (
          <NavLink key={item.to} to={item.to} className={navClass}
            onClick={onNavigate} data-testid={item.testid}>
            <item.icon className="h-4 w-4" aria-hidden="true" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <Separator />
      <div className="p-3">
        <div className="flex items-center gap-3 rounded-lg px-2 py-2">
          <UserAvatar name={user?.name} src={user?.avatar_url} className="h-9 w-9 text-sm" data-testid="customer-sidebar-avatar" />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium" data-testid="customer-sidebar-name">{user?.name || 'Cliente'}</p>
            <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
          </div>
        </div>
        <Button variant="ghost" className="mt-1 w-full justify-start gap-2 text-muted-foreground"
          onClick={onLogout} data-testid="customer-logout-button">
          <LogOut className="h-4 w-4" /> Esci
        </Button>
      </div>
    </div>
  );
}

export function CustomerLayout() {
  const { user, logout } = useAuth();
  const { count, openMiniCart } = useCart() || { count: 0, openMiniCart: () => {} };
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate('/cliente/accedi');
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b bg-background/85 px-3 backdrop-blur lg:hidden">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="customer-menu-toggle" aria-label="Apri menu">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0">
            <SidebarContent user={user} onLogout={doLogout} onNavigate={() => setOpen(false)} cartCount={count} />
          </SheetContent>
        </Sheet>
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Mountain className="h-4 w-4" />
          </div>
          <span className="font-display text-sm font-bold">Area Clienti</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="relative" onClick={openMiniCart}
            data-testid="customer-header-cart" aria-label="Carrello">
            <ShoppingCart className="h-5 w-5" />
            {count > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground tabular-nums"
                data-testid="customer-header-cart-badge">{count}</span>
            )}
          </Button>
          <NotificationsBell />
        </div>
      </header>

      <div className="lg:grid lg:grid-cols-[18rem_1fr]">
        <aside className="sticky top-0 hidden h-screen border-r bg-card/60 backdrop-blur lg:block">
          <SidebarContent user={user} onLogout={doLogout} cartCount={count} />
        </aside>
        <main className="min-w-0">
          <div className="mx-auto max-w-5xl overflow-x-hidden px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
